import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaTextarea } from "@/shared/components/forms/ColocaTextarea";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { CANCEL_REASON_MAX_LENGTH } from "../../constants/payoutBatches.constants";

export interface CancelBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
  isPending: boolean;
  errorMessage: string | null;
}

/** Cancelling pays nobody, so no step-up code: an optional reason, one confirmation. */
export function CancelBatchDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  errorMessage,
}: CancelBatchDialogProps) {
  const { t } = useTranslations("payoutBatches");
  const [reason, setReason] = useState("");

  const handleOpenChange = (next: boolean) => {
    if (!next && isPending) return;
    if (!next) setReason("");
    onOpenChange(next);
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="sm"
      title={t("cancel_title")}
      description={t("cancel_description")}
      showCloseButton={!isPending}
      footer={
        <>
          <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={isPending} />}>
            {t("action_close")}
          </ColocaDialogClose>
          <ColocaButton variant="destructive" loading={isPending} onClick={() => void onConfirm(reason)}>
            {t("cancel_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-2">
        <label htmlFor="payout-batch-cancel-reason" className="text-sm font-medium text-foreground">
          {t("cancel_reason_label")}
        </label>
        <ColocaTextarea
          id="payout-batch-cancel-reason"
          value={reason}
          onValueChange={setReason}
          placeholder={t("cancel_reason_placeholder")}
          maxLength={CANCEL_REASON_MAX_LENGTH}
          rows={3}
          disabled={isPending}
        />
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      </div>
    </ColocaDialog>
  );
}
